import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Badge,
  Input,
  Select,
  Skeleton,
  Pagination,
  EmptyState,
  Alert,
} from '@bbpos/ui';
import type { Product } from '@bbpos/types';
import { productsService, type ProductListFilters } from '@/services/products.service';
import { useLocationStore } from '@/stores/use-location-store';
import { useNetworkStore } from '@/stores/use-network-store';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatPriceRange } from '@/lib/format';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { getStockBadge, isLowStock, isOutOfStock } from '@/lib/product-helpers';
import { ChevronLeftIcon, PackageIcon } from '@/lib/icons';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const STOCK_STATUS_OPTIONS = [
  { value: '', label: 'All Stock' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'low_stock', label: 'Low Stock' },
  { value: 'out_of_stock', label: 'Out of Stock' },
];

const ACTIVE_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ─── Component ─────────────────────────────────────────────────────────────

export function ProductCatalogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLocationId = useLocationStore((s) => s.currentLocationId);
  const isConnected = useNetworkStore((s) => s.isConnected);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();

  // ── Read filters from URL ─────────────────────────────────────────────
  const urlSearch = searchParams.get('search') ?? '';
  const urlCategory = searchParams.get('category') ?? '';
  const urlStockStatus = searchParams.get('stock_status') ?? '';
  const urlIsActive = searchParams.get('is_active') ?? '';
  const urlPage = Number(searchParams.get('page')) || 1;

  // ── Local state ───────────────────────────────────────────────────────
  const [search, setSearch] = useState(urlSearch);
  const [category, setCategory] = useState(urlCategory);
  const [stockStatus, setStockStatus] = useState(urlStockStatus);
  const [isActive, setIsActive] = useState(urlIsActive);
  const [page, setPage] = useState(urlPage);

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<{ total: number; low: number; out: number } | null>(null);

  // ── Fetch categories once ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchCategories() {
      try {
        const cats = await productsService.getCategories(currentLocationId ?? undefined);
        if (cancelled) return;
        setCategories([
          { value: '', label: 'All Categories' },
          ...cats.map((c) => ({ value: c, label: c })),
        ]);
      } catch {
        // Silently fail — categories will be empty, user can still search
      }
    }
    fetchCategories();
    return () => { cancelled = true; };
  }, [currentLocationId]);

  // ── Fetch KPI counts once per location ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchCounts() {
      const base: ProductListFilters = { page: 1, limit: 1, is_active: 'true' };
      if (currentLocationId) base.location_id = currentLocationId;
      try {
        const [all, low, out] = await Promise.all([
          productsService.list(base),
          productsService.list({ ...base, stock_status: 'low_stock' }),
          productsService.list({ ...base, stock_status: 'out_of_stock' }),
        ]);
        if (cancelled) return;
        setCounts({ total: all.meta.total, low: low.meta.total, out: out.meta.total });
      } catch {
        // Non-critical — KPI cards simply stay hidden
      }
    }
    fetchCounts();
    return () => { cancelled = true; };
  }, [currentLocationId]);

  // ── Debounce search ───────────────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isFirstRender = useRef(true);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // ── Sync filters to URL ───────────────────────────────────────────────
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (category) params.category = category;
    if (stockStatus) params.stock_status = stockStatus;
    if (isActive) params.is_active = isActive;
    if (page > 1) params.page = String(page);
    setSearchParamsRef.current(params, { replace: true });
  }, [debouncedSearch, category, stockStatus, isActive, page]);

  // ── Fetch products ────────────────────────────────────────────────────
  // Offline the list keeps what it last loaded (no request, no error); coming
  // back online refetches, so server-side changes arrive without a reload.
  useEffect(() => {
    if (!isConnected) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchProducts() {
      setIsLoading(true);
      setError(null);
      try {
        const filters: ProductListFilters = {
          page,
          limit: PAGE_SIZE,
        };
        if (currentLocationId) filters.location_id = currentLocationId;
        if (debouncedSearch) filters.search = debouncedSearch;
        if (category) filters.category = category;
        if (isActive) filters.is_active = isActive;
        if (stockStatus) filters.stock_status = stockStatus;

        const result = await productsService.list(filters);
        if (cancelled) return;

        setProducts(result.data);
        setMeta(result.meta);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Failed to load products. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchProducts();
    return () => { cancelled = true; };
  }, [debouncedSearch, category, isActive, stockStatus, page, currentLocationId, isConnected]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleCategoryChange = useCallback((val: string | string[]) => {
    setCategory(typeof val === 'string' ? val : val[0] ?? '');
    setPage(1);
  }, []);

  const handleStockStatusChange = useCallback((val: string | string[]) => {
    setStockStatus(typeof val === 'string' ? val : val[0] ?? '');
    setPage(1);
  }, []);

  const handleActiveChange = useCallback((val: string | string[]) => {
    setIsActive(typeof val === 'string' ? val : val[0] ?? '');
    setPage(1);
  }, []);

  const hasActiveFilters = !!(debouncedSearch || category || stockStatus || isActive);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setCategory('');
    setStockStatus('');
    setIsActive('');
    setPage(1);
  }, []);

  const handleRowClick = useCallback((productId: string) => {
    navigate(`/pos/products/${productId}`);
  }, [navigate]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      {/* ═══ Header ═══ */}
      <header className="shrink-0 bg-gradient-to-r from-brand-600 to-indigo-600 px-6 py-4 shadow-lg animate-fade-in">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pos/billing')}
            className="text-white/80 hover:text-white hover:bg-white/10"
            aria-label="Back to POS"
          >
            <ChevronLeftIcon />
            <span className="ml-1 hidden sm:inline">POS</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white">Product Catalog</h1>
            <p className="text-sm text-white/70 mt-0.5 hidden sm:block">
              Browse prices, stock and tax for every product at this location
            </p>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-sm">
            <Input
              variant="search"
              value={search}
              onChange={setSearch}
              placeholder="Search name, SKU, barcode..."
            />
          </div>
        </div>

        {/* KPI cards */}
        {counts && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20 transition-all duration-200 hover:bg-white/15">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white">
                  <PackageIcon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-white/80">Active Products</span>
              </div>
              <p className="text-xl font-bold text-white tabular-nums mt-1">{counts.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20 transition-all duration-200 hover:bg-white/15">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-warning-500 flex items-center justify-center text-white">
                  <PackageIcon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-white/80">Low Stock</span>
              </div>
              <p className="text-xl font-bold text-white tabular-nums mt-1">{counts.low}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20 transition-all duration-200 hover:bg-white/15">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-danger-500 flex items-center justify-center text-white">
                  <PackageIcon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-white/80">Out of Stock</span>
              </div>
              <p className="text-xl font-bold text-white tabular-nums mt-1">{counts.out}</p>
            </div>
          </div>
        )}
      </header>

      {/* ═══ Filters ═══ */}
      <div className="shrink-0 flex flex-wrap items-end gap-3 px-6 py-3 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="w-44">
          <Select
            options={categories.length > 0 ? categories : [{ value: '', label: 'All Categories' }]}
            value={category}
            onChange={handleCategoryChange}
            placeholder="All Categories"
            clearable
          />
        </div>
        <div className="w-40">
          <Select
            options={STOCK_STATUS_OPTIONS}
            value={stockStatus}
            onChange={handleStockStatusChange}
            placeholder="All Stock"
            clearable
          />
        </div>
        <div className="w-36">
          <Select
            options={ACTIVE_OPTIONS}
            value={isActive}
            onChange={handleActiveChange}
            placeholder="All Status"
            clearable
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      {/* ═══ Error banner ═══ */}
      {error && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} />
        </div>
      )}

      {/* ═══ Products List ═══ */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton variant="table-row" count={8} animated />
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<PackageIcon />}
            title={isConnected ? 'No products found' : "You're offline"}
            description={
              isConnected
                ? 'Try adjusting your filters or search query.'
                : 'The catalogue loads when the connection is back. Billing keeps working offline.'
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[34%]" />
                  <col className="w-[13%]" />
                  <col className="w-[13%]" />
                  <col className="w-[11%]" />
                  <col className="w-[9%]" />
                  <col className="w-[6%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead className="bg-neutral-100 dark:bg-neutral-800 sticky top-0 z-10">
                  <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    <th className="px-6 py-3">Product</th>
                    <th className="px-6 py-3">SKU</th>
                    <th className="px-6 py-3">Category</th>
                    <th className="px-6 py-3 text-right">Price</th>
                    <th className="px-6 py-3 text-right">Stock</th>
                    <th className="px-6 py-3 text-right">{tax.taxName}</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {products.map((product) => (
                    <tr
                      key={product.id}
                      onClick={() => handleRowClick(product.id)}
                      className="bg-white dark:bg-neutral-900 even:bg-neutral-50 dark:even:bg-neutral-800/50 hover:bg-brand-50 dark:hover:bg-brand-900/20 focus-within:bg-brand-50 dark:focus-within:bg-brand-900/20 cursor-pointer transition-all duration-200 animate-slide-up"
                    >
                      <td className="px-6 py-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(product.id);
                          }}
                          aria-label={`Open product details for ${product.name}`}
                          className="w-full text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        >
                          <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="h-10 w-10 rounded object-cover shrink-0"
                            />
                          ) : (
                            <div
                              className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500 to-success-500 text-white font-semibold text-sm flex items-center justify-center shrink-0"
                              aria-hidden="true"
                            >
                              {product.name.trim().charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="font-medium text-neutral-900 dark:text-neutral-100 block truncate">
                              {product.name}
                            </span>
                            {(product.is_variant || product.parent_product_id || product.hide_in_pos || product.hide_in_sales) && (
                              <div className="flex items-center gap-1 mt-0.5">
                                {(product.is_variant || product.parent_product_id) && (
                                  <Badge variant="info" size="sm">Variant</Badge>
                                )}
                                {product.hide_in_pos && <Badge variant="neutral" size="sm">Hidden in POS</Badge>}
                                {product.hide_in_sales && <Badge variant="neutral" size="sm">Hidden in Sales</Badge>}
                              </div>
                            )}
                          </div>
                          </div>
                        </button>
                      </td>
                      <td className="px-6 py-3 text-neutral-500 dark:text-neutral-400 text-xs font-mono">
                        {product.sku}
                      </td>
                      <td className="px-6 py-3 text-neutral-600 dark:text-neutral-400">
                        {product.category || '—'}
                      </td>
                      <td className="px-6 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100">
                        {product.variant_price_min != null && product.variant_price_max != null
                          ? formatPriceRange(product.variant_price_min, product.variant_price_max, currency)
                          : formatCurrency(product.price, currency)}
                      </td>
                      <td
                        className={`px-6 py-3 text-right tabular-nums ${
                          isOutOfStock(product)
                            ? 'font-semibold text-danger-600 dark:text-danger-400'
                            : isLowStock(product)
                              ? 'font-semibold text-warning-600 dark:text-warning-400'
                              : 'text-neutral-700 dark:text-neutral-300'
                        }`}
                      >
                        {product.stock_quantity}
                        <span className="ml-1 text-xs font-normal text-neutral-400">{product.unit}</span>
                      </td>
                      <td className="px-6 py-3 text-right text-neutral-500 dark:text-neutral-400">
                        {product.gst_rate}%
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5">
                          {getStockBadge(product)}
                          {!product.is_active && <Badge variant="draft" size="sm" dot>Inactive</Badge>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden px-4 py-3 space-y-2">
              {products.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleRowClick(product.id)}
                  className="w-full text-left bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:shadow-md transition-all duration-200 animate-slide-up"
                >
                  <div className="flex items-start gap-3">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="h-10 w-10 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div
                        className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500 to-success-500 text-white font-semibold text-sm flex items-center justify-center shrink-0"
                        aria-hidden="true"
                      >
                        {product.name.trim().charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate">
                          {product.name}
                        </span>
                        <span className="font-semibold text-sm text-neutral-900 dark:text-neutral-100 ml-2 shrink-0">
                          {product.variant_price_min != null && product.variant_price_max != null
                            ? formatPriceRange(product.variant_price_min, product.variant_price_max, currency)
                            : formatCurrency(product.price, currency)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                        <span className="font-mono">{product.sku}</span>
                        {product.category && <span>{product.category}</span>}
                        <span>{tax.taxName} {product.gst_rate}%</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {getStockBadge(product)}
                        {!product.is_active && <Badge variant="draft" size="sm">Inactive</Badge>}
                        {product.hide_in_pos && <Badge variant="neutral" size="sm">Hidden in POS</Badge>}
                        {product.hide_in_sales && <Badge variant="neutral" size="sm">Hidden in Sales</Badge>}
                        {(product.is_variant || product.parent_product_id) && (
                          <Badge variant="info" size="sm">Variant</Badge>
                        )}
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-auto">
                          Qty: {product.stock_quantity} {product.unit}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ═══ Pagination ═══ */}
      {!isLoading && meta.totalPages > 0 && (
        <div className="shrink-0 px-6 py-3 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
          <Pagination
            currentPage={meta.page}
            totalPages={meta.totalPages}
            totalItems={meta.total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
